import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms'
import { ActivatedRoute, Router, RouterLink } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'
import { EMPTY, catchError, filter, finalize, switchMap, take } from 'rxjs'

import type { UserDto } from '../../../../api/responses'
import { InputFieldComponent } from '../../../../components/input-field'
import { SubmitButtonComponent } from '../../../../components/submit-button/submit-button.component'
import { NameValidator, PhoneValidator } from '../../../../misc/validators'
import { AuthService } from '../../../../services/auth/auth.service'
import type { EditableFieldDefinition } from '../../../../types/field-types'
import { getArticleIdFromRoute } from '../../../auth/util/article-purchase-route.utils'
import { ArticlePurchaseAccountShellComponent } from '../article-purchase-account-shell.component'

const requiredValidator: ValidatorFn = (control: AbstractControl) =>
  Validators.required(control)
const emailValidator: ValidatorFn = (control: AbstractControl) =>
  Validators.email(control)

@Component({
  selector: 'dhp-article-purchase-account-update',
  templateUrl: './article-purchase-account-update.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    TranslatePipe,
    InputFieldComponent,
    SubmitButtonComponent,
    ArticlePurchaseAccountShellComponent,
  ],
})
export class ArticlePurchaseAccountUpdateComponent {
  private readonly route = inject(ActivatedRoute)
  private readonly router = inject(Router)
  private readonly authService = inject(AuthService)
  private readonly destroyRef = inject(DestroyRef)

  readonly articleId = signal<number | null>(getArticleIdFromRoute(this.route))
  readonly submitting = signal(false)
  readonly errorKey = signal<string | null>(null)
  readonly checkoutUrl = computed(() =>
    this.articleId() !== null
      ? `/articles/${String(this.articleId())}/purchase`
      : '/articles'
  )

  readonly formGroup = new FormGroup({
    first_name: new FormControl<string | null>(null, [
      requiredValidator,
      NameValidator,
    ]),
    last_name: new FormControl<string | null>(null, [
      requiredValidator,
      NameValidator,
    ]),
    email: new FormControl<string | null>(null, [
      requiredValidator,
      emailValidator,
    ]),
    phone_number: new FormControl<string | null>(null, [PhoneValidator]),
  })

  readonly fields: EditableFieldDefinition[] = [
    {
      labelKey: 'pages.account.profile.details.firstName',
      formControlName: 'first_name',
      showRequiredIndicator: true,
      type: 'text',
      placeholderKey: 'common.fieldPlaceholder.firstName',
      errorKeys: {
        required: 'common.fieldValidation.required',
        pattern: 'common.fieldValidation.invalidCharactersError.pattern',
      },
    },
    {
      labelKey: 'pages.account.profile.details.lastName',
      formControlName: 'last_name',
      showRequiredIndicator: true,
      type: 'text',
      placeholderKey: 'common.fieldPlaceholder.lastName',
      errorKeys: {
        required: 'common.fieldValidation.required',
        pattern: 'common.fieldValidation.invalidCharactersError.pattern',
      },
    },
    {
      labelKey: 'pages.account.profile.details.email',
      formControlName: 'email',
      showRequiredIndicator: true,
      type: 'text',
      placeholderKey: 'common.fieldPlaceholder.email',
      errorKeys: {
        required: 'common.fieldValidation.required',
        email: 'common.fieldValidation.email',
        sameEmail: 'pages.articlePurchase.accountUpdate.errors.sameEmail',
      },
    },
    {
      labelKey: 'pages.account.profile.details.phoneNumber',
      formControlName: 'phone_number',
      type: 'text',
      placeholderKey: 'common.fieldPlaceholder.phoneNumber',
      errorKeys: {
        pattern: 'common.fieldValidation.phoneNumberErrors.pattern',
      },
    },
  ]

  constructor() {
    this.patchFormWithUser(this.authService.currentUser())

    this.authService.currentUser$
      .pipe(
        filter((user): user is UserDto => !!user),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(user => {
        this.patchFormWithUser(user)

        if (!user.managed) {
          void this.navigateToCheckout()
        }
      })
  }

  submit(): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched()
      return
    }

    if (this.submitting()) {
      return
    }

    const emailControl = this.formGroup.controls.email
    const email = this.normalizeInput(emailControl.value)
    if (!email || this.authService.currentUser()?.email === email) {
      emailControl.setErrors(email ? { sameEmail: true } : { required: true })
      emailControl.markAsTouched()
      return
    }

    this.submitting.set(true)
    this.errorKey.set(null)

    const firstName = this.normalizeInput(
      this.formGroup.controls.first_name.value
    )
    const lastName = this.normalizeInput(
      this.formGroup.controls.last_name.value
    )
    const name = [firstName, lastName].join(' ')

    this.authService
      .updateUser({
        name,
        first_name: firstName,
        last_name: lastName,
        email,
        phone_number: this.normalizeInput(
          this.formGroup.controls.phone_number.value
        ),
      })
      .pipe(
        take(1),
        switchMap(() => this.authService.refreshUserDetails()),
        catchError((error: unknown) => {
          console.error('Error updating account for article purchase', error)
          this.errorKey.set('pages.articlePurchase.accountUpdate.errors.submit')
          return EMPTY
        }),
        finalize(() => {
          this.submitting.set(false)
        })
      )
      .subscribe(() => {
        void this.navigateToCheckout()
      })
  }

  private patchFormWithUser(user: UserDto | null | undefined): void {
    if (!user) {
      return
    }

    this.formGroup.patchValue(
      {
        first_name: user.first_name ?? null,
        last_name: user.last_name ?? null,
        email: null,
        phone_number: user.phone_number ?? null,
      },
      { emitEvent: false }
    )
  }

  private navigateToCheckout(): Promise<boolean> {
    const articleId = this.articleId()

    return this.router.navigate(
      articleId !== null ? ['/articles', articleId, 'purchase'] : ['/articles'],
      {
        queryParams: {
          accountFlow: 'update',
        },
      }
    )
  }

  private normalizeInput(value: string | null | undefined): string | null {
    const normalized = value?.trim()
    return normalized ?? null
  }
}
